import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { StoryPreview } from './stories/StoryPreview';
import SearchBar from './SearchBar';
import * as S from './styles';
import * as StoryS from './stories/styles';

const StoriesView = () => {
	const [selectedStoryIndex, setSelectedStoryIndex] = useState<number | null>(null);
	const designSystemContent = useSelector((r: RootReducer) => r.mode.designSystemContent);
	const { searchQuery } = useSelector((r: RootReducer) => r.mode.designSystemFilters.stories);

	const allStories = designSystemContent?.stories || [];
	const rawCode = designSystemContent?.rawCode || {
		tokens: '',
		primitives: '',
		components: '',
	};

	// Filter stories based on search query
	const stories = useMemo(() => {
		if (!searchQuery.trim()) {
			return allStories;
		}
		const query = searchQuery.toLowerCase();
		return allStories.filter(
			(story) =>
				story.title.toLowerCase().includes(query) || story.code.toLowerCase().includes(query)
		);
	}, [allStories, searchQuery]);

	// Auto-select first story if available and none selected
	React.useEffect(() => {
		if (stories.length > 0 && selectedStoryIndex === null) {
			setSelectedStoryIndex(0);
		} else if (stories.length === 0) {
			setSelectedStoryIndex(null);
		} else if (selectedStoryIndex !== null && selectedStoryIndex >= stories.length) {
			// If selected index is out of bounds, reset to first
			setSelectedStoryIndex(0);
		}
	}, [stories.length, selectedStoryIndex]);

	if (stories.length === 0) {
		return (
			<S.SectionBase>
				<S.SectionHeader>
					<S.SectionTitle>Stories</S.SectionTitle>
					<SearchBar section="stories" placeholder="Search stories..." />
				</S.SectionHeader>
				<S.SectionContent>
					<StoryS.EmptyState>
						No stories found{searchQuery && ` matching "${searchQuery}"`}.
					</StoryS.EmptyState>
				</S.SectionContent>
			</S.SectionBase>
		);
	}

	const selectedStory = selectedStoryIndex !== null ? stories[selectedStoryIndex] : null;

	return (
		<S.SectionBase style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
			<div style={{ padding: '20px 20px 0 20px' }}>
				<S.SectionHeader>
					<S.SectionTitle>Stories</S.SectionTitle>
					<SearchBar section="stories" placeholder="Search stories..." />
				</S.SectionHeader>
			</div>
			<StoryS.StoriesContainer>
				<StoryS.StoryList>
					<StoryS.StoryIndex>Stories ({stories.length})</StoryS.StoryIndex>
					{stories.map((story, index) => (
						<StoryS.StoryItem
							key={index}
							selected={selectedStoryIndex === index}
							isBroken={story.isValidHtml === false}
							onClick={() => setSelectedStoryIndex(index)}
						>
							<StoryS.StoryItemTitle>{story.title}</StoryS.StoryItemTitle>
							{story.isValidHtml === false && (
								<StoryS.StoryItemBadge>⚠ Broken</StoryS.StoryItemBadge>
							)}
						</StoryS.StoryItem>
					))}
				</StoryS.StoryList>
				<StoryS.StoryPreviewContainer>
					{selectedStory ? (
						<StoryS.StoryPreviewFrame>
							<StoryPreview
								storyHtml={selectedStory.code}
								isValidHtml={selectedStory.isValidHtml}
								rawCode={rawCode}
							/>
						</StoryS.StoryPreviewFrame>
					) : (
						<StoryS.EmptyState>Select a story to preview</StoryS.EmptyState>
					)}
				</StoryS.StoryPreviewContainer>
			</StoryS.StoriesContainer>
		</S.SectionBase>
	);
};

export default StoriesView;
